import { Box, Grid as MuiGrid } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { FunctionComponent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import withSizes from 'react-sizes';

import GridV2 from '@@src/components/Grid/GridV2';
import { useAppSelector } from '@@src/hooks/store';
import useFavouritesCollection from '@@src/hooks/useFavouritesCollection';
import PageLayout from '@@src/pages/PageLayout';
import { getIsLoggedIn, getIsReady } from '@@stores/UserStore';
import { SSRFunctionComponent } from '@@types/ssr';
import { mapSizesToProps } from '@@utils/helpers';

import backgroundImage16x9 from '../../static/images/background-image-favourites-16x9.jpg';
import backgroundImage2x3 from '../../static/images/background-image-favourites-2x3.jpg';
import { RtlThemeProvider, ThemeProviderWrapper } from '../ProviderWrapper';
import Button from '../components/Inputs/Button';
import { dispatchOpenCreateAccount, dispatchOpenSignIn } from '../components/Login/LoginForm';
import AlternateLinks from '../components/Utils/AlternateLinks';
import OdContainer from '../components/Utils/OdContainer';
import { isRtl } from '../i18n';
import { ReactComponent as FavouriteIcon } from '../images/icons/favourite.svg';
import orange from '../styles/colors/orange';
import fontFamily from '../styles/typography/fontFamily';

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    heading: {
      marginTop: 80,
      marginBottom: '2vw',
      fontFamily: fontFamily.secondary,
    },
    signInContainer: {
      textAlign: 'center',
      marginBottom: theme.spacing(20),
    },
    signInHeading: {
      marginTop: theme.spacing(7),
      fontSize: '1.25rem',
      fontWeight: 'bold',
      fontFamily: fontFamily.secondary,
    },
    signInDescription: {
      marginTop: theme.spacing(3),
      fontSize: '1.125rem',
      lineHeight: 1.38,
      color: 'rgba(255, 255, 255, 0.6)',
      fontFamily: fontFamily.primary,
    },
    emptyContainer: {
      textAlign: 'center',
      marginTop: theme.spacing(4),
      marginLeft: 'auto',
      marginRight: 'auto',
      marginBottom: theme.spacing(20),
    },
    favouriteIcon: {
      opacity: 1,
      fontSize: '13em',
      '& [class*="circle"]': {
        stroke: 'transparent',
        fill: 'rgba(255, 255, 255, 0.1)',
      },
      '& [class*="heart"]': {
        stroke: orange.darkTangerine,
        fill: orange.darkTangerine,
      },
    },
    emptyHeading: {
      fontWeight: 'bold',
      fontSize: '1.25rem',
      marginTop: theme.spacing(7),
      fontFamily: fontFamily.secondary,
    },
    emptyDescription: {
      marginTop: theme.spacing(2),
      fontSize: '1.125rem',
      color: 'rgba(255, 255, 255, 0.6)',
    },
    favouriteButton: {},
  });
});

interface FavouritesProps {
  windowWidth?: number;
  breakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

// Favourite content for non logged in user
const GuestFavouriteContent: FunctionComponent = (props) => {
  const classes = useStyles(props);
  const { t } = useTranslation(['page', 'common']);

  return (
    <div className={classes.signInContainer}>
      <div className={classes.signInHeading}>{t('favourites.signInHeading', { context: 'updated' })}</div>
      <div className={classes.signInDescription}>
        <div>
          {t('favourites.signInNote1')}
          &nbsp;
          {t('favourites.signInNote2')}
        </div>
      </div>
      <Box mt={8}>
        <MuiGrid container spacing={3} justifyContent="center">
          <MuiGrid item xs={12} sm="auto">
            <Button
              onClick={() => {
                dispatchOpenSignIn();
              }}
            >
              {t('common:button.signIn')}
            </Button>
          </MuiGrid>
          <MuiGrid item xs={12} sm="auto">
            <Button
              buttonType="secondary"
              onClick={() => {
                dispatchOpenCreateAccount();
              }}
            >
              {t('common:button.createAccount')}
            </Button>
          </MuiGrid>
        </MuiGrid>
      </Box>
    </div>
  );
};

const UserFavouriteContent: FunctionComponent = withSizes(mapSizesToProps)((props) => {
  const classes = useStyles(props);
  const { t, i18n: { language } } = useTranslation(['page']);

  const { loading, favourites: items } = useFavouritesCollection();

  if (loading) {
    return null;
  }

  if (items.length === 0 && !loading) {
    return (
      <div className={classes.emptyContainer}>
        <FavouriteIcon className={classes.favouriteIcon}/>
        <div className={classes.emptyHeading}>{t('favourites.emptyHeading', { context: 'updated' })}</div>
        <div className={classes.emptyDescription}>{t('favourites.emptyDescription', { context: 'updated' })}</div>
      </div>
    );
  }

  if (items.length && !loading) {
    const favouriteContent = (
      <GridV2
        buttonType="close"
        collection={{
          id: '?',
          layout: 'grid',
          title: 'string',
          displayType: '16:9',
          route: {} as any,
          items,
        }}
      />
    );

    if (isRtl(language)) {
      // revert the grid back to ltr for rtl language
      return (
        <div dir="ltr">
          <ThemeProviderWrapper>
            {favouriteContent}
          </ThemeProviderWrapper>
        </div>
      );
    }

    return favouriteContent;
  }

  return null;
});

const FavouritesPage: SSRFunctionComponent<FavouritesProps> = (props) => {
  const {
    onLoadSuccess = () => {},
  } = props;
  const classes = useStyles(props);
  const { t, i18n: { language } } = useTranslation(['page']);

  const loggedIn = useAppSelector(getIsLoggedIn);
  const userStateReady = useAppSelector(getIsReady);

  const heading = t('favourites.heading');

  const heroImage = {
    '2:3': backgroundImage2x3,
    '16:9': backgroundImage16x9,
  };

  const path = useLocation().pathname;

  useEffect(() => {
    onLoadSuccess(heading);
  }, [heading, onLoadSuccess]);

  return (
    <div>
      <AlternateLinks/>
      <PageLayout title={heading} path={path} heroImage={heroImage}>
        {userStateReady && loggedIn ? (
          <OdContainer dir={isRtl(language) ? 'rtl' : 'ltr'}>
            <UserFavouriteContent/>
          </OdContainer>
        ) : (
          <OdContainer className={classes.heading} dir={isRtl(language) ? 'rtl' : 'ltr'}>
            <GuestFavouriteContent/>
          </OdContainer>
        )}
      </PageLayout>
    </div>
  );
};

const FavouritesPageWithRtlProvider: SSRFunctionComponent<FavouritesProps> = (props) => {
  return <RtlThemeProvider><FavouritesPage {...props}/></RtlThemeProvider>;
};

export default FavouritesPageWithRtlProvider;
